import { useState } from 'react';

export type ValidationRule = (value: string) => { isValid: boolean; errorMessage?: string };

export type FormValidationConfig<T> = {
  [K in keyof T]?: {
    required?: boolean;
    requiredMessage?: string;
    validate?: ValidationRule;
  };
};

export function useFormValidation<T extends Record<string, TValue>, TValue = unknown>(config: FormValidationConfig<T>) {
  const [errors, setErrors] = useState<Partial<Record<keyof T, string>>>({});
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  function validateForm(formData: FormData): Partial<Record<keyof T, string>> {
    const newErrors: Partial<Record<keyof T, string>> = {};

    for (const [fieldName, fieldConfig] of Object.entries(config)) {
      const value = formData.get(fieldName) as string;

      // Check required fields
      if (fieldConfig?.required && (!value || value.trim() === '')) {
        newErrors[fieldName as keyof T] = fieldConfig.requiredMessage || `${fieldName} is required`;
        continue;
      }

      // Run custom validation
      if (fieldConfig?.validate && value) {
        const result = fieldConfig.validate(value);
        if (!result.isValid) {
          newErrors[fieldName as keyof T] = result.errorMessage || `Invalid ${fieldName}`;
        }
      }
    }

    return newErrors;
  }

  function resetErrors() {
    setErrors({});
  }

  return {
    errors,
    setErrors,
    isSubmitting,
    setIsSubmitting,
    validateForm,
    resetErrors,
  };
}
